/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Assignment form styles
 *
 * @file assignmentFormStyles.js
 * @author Gladson Samuel S
 * @since 2.0.0
 */

import { styled } from '@mui/material/styles';
import mixins from '../../../assets/styles/mixins.scss';
import { createScopedClasses, createScopedStyles } from '../../../assets/styles/createScopedStyles';

const assignmentFormStyles = {
  formPane: {
    backgroundColor: mixins.chalkWhiteFont,
    borderRadius: 16,
    padding: 24,
    boxSizing: 'border-box',
    minWidth: 0,
    position: 'relative'
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    minHeight: 40,
    gap: 8
  },
  // Back nav beside the form title; matches the DashboardCard header back button.
  backNavButton: {
    color: mixins.navyColor,
    maxHeight: 40,
    maxWidth: 40,
    flexShrink: 0,
    '&:hover': {
      background: mixins.secondaryButtonHoverColor
    },
    '& > span': {
      minWidth: 'unset'
    }
  },
  backNavIcon: {
    width: 44,
    height: 44
  },
  sectionTitle: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 20,
    fontWeight: mixins.fontWeight500,
    lineHeight: '28px',
    letterSpacing: '-0.4px',
    color: mixins.color_text_primary,
    margin: 0
  },
  addContentRow: {
    marginTop: 0,
    marginBottom: 16
  },
  contentSection: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 16
  },
  addContentButton: {
    borderRadius: 1000,
    border: `2px solid ${mixins.lightTextColor2}`,
    padding: '8px 12px',
    textTransform: 'none',
    color: mixins.lightTextColor2,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 14,
    fontWeight: mixins.fontWeight500,
    lineHeight: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 'unset',
    maxHeight: 32,
    backgroundColor: 'transparent',

    '&:hover': {
      backgroundColor: mixins.lightThemeBgColor2
    },

    '&:disabled': {
      borderColor: mixins.disabledInputBoxColor,
      color: mixins.disabledTextColor,
      pointerEvents: 'unset',

      '& svg': {
        opacity: 0.5
      }
    },

    '& svg': {
      fontSize: 14
    }
  },
  formBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16
  },
  dueDateRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'flex-start'
  },
  dateField: {
    minWidth: 220,
    flex: '0 0 220px'
  },
  timeField: {
    minWidth: 220,
    flex: '0 0 220px'
  },
  timeZoneField: {
    width: '100%',
    maxWidth: 448
  },
  assignmentNameField: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  assignmentNameLabel: {
    fontWeight: mixins.fontWeight500,
    fontSize: mixins.fontSize16,
    fontFamily: mixins.fontFamilyPlusSans,
    color: mixins.lightTextColor2,
    lineHeight: '20px',
    paddingBottom: 4,
    display: 'block'
  },
  readonlyInput: {
    width: '100%',
    boxSizing: 'border-box',
    borderRadius: 4,
    padding: 12,
    backgroundColor: mixins.chalkWhiteFont,
    border: `1px solid ${mixins.amethyst}`,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight400,
    fontFamily: mixins.fontFamilyPlusSans,
    color: mixins.greyScaleColor,
    outline: 'none',
    cursor: 'default'
  },
  readonlyInputDisabled: {
    width: '100%',
    boxSizing: 'border-box',
    borderRadius: 4,
    padding: 12,
    backgroundColor: mixins.bulkEditCtaButtonDisabledBGColor,
    border: `1px solid ${mixins.disabledInputBoxColor}`,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight400,
    fontFamily: mixins.fontFamilyPlusSans,
    color: mixins.disabledTextColor,
    outline: 'none',
    cursor: 'not-allowed'
  },
  assignmentNameInputWrap: {
    position: 'relative',
    width: '100%'
  },
  assignmentNameLoader: {
    position: 'absolute',
    right: 12,
    top: '50%',
    marginTop: -10
  },
  assignmentNameInput: {
    width: '100%',
    boxSizing: 'border-box',
    borderRadius: 4,
    padding: 12,
    backgroundColor: mixins.chalkWhiteFont,
    border: `1px solid ${mixins.amethyst}`,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight400,
    fontFamily: mixins.fontFamilyPlusSans,
    color: mixins.color_text_primary,
    outline: 'none',

    '&:focus': {
      borderColor: mixins.amethystColorV2
    }
  },
  assignmentNameInputError: {
    borderColor: mixins.darkNeedsReviewBgColor,

    '&:focus': {
      borderColor: mixins.darkNeedsReviewBgColor
    }
  },
  assignmentNameErrorText: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    color: mixins.accentRed,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 12,
    fontWeight: mixins.fontWeight500,
    lineHeight: '20px'
  },
  assignmentNameErrorIcon: {
    fontSize: 16,
    flexShrink: 0
  },
  actionsRow: {
    display: 'flex',
    gap: 12,
    alignItems: 'center',
    flexWrap: 'wrap',
    marginTop: 32
  },
  finishButton: {
    borderRadius: 1000,
    border: `2px solid ${mixins.lightTextColor2}`,
    color: mixins.lightTextColor2,
    padding: '16px 24px',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: '16px',
    textTransform: 'none',
    backgroundColor: 'transparent',
    maxHeight: 48,

    '&:hover': {
      backgroundColor: mixins.lightThemeBgColor2
    },
    '&:disabled': {
      borderColor: mixins.disabledInputBoxColor,
      color: mixins.disabledTextColor
    },
    '& span': {
      minWidth: 'unset'
    }
  },
  saveAndAddButton: {
    borderRadius: 1000,
    backgroundColor: mixins.amethyst,
    color: mixins.chalkWhiteFont,
    padding: '16px 24px',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: '16px',
    textTransform: 'none',
    maxHeight: 48,

    '&:hover': {
      backgroundColor: mixins.amethystColorV2,
      color: mixins.chalkWhiteFont
    },
    '&:disabled': {
      backgroundColor: mixins.bulkEditCtaButtonDisabledBGColor,
      color: mixins.disabledTextColor
    }
  },
  actionLoader: {
    marginLeft: 8,
    color: 'inherit',
    flexShrink: 0
  },
  cancelButton: {
    borderRadius: 1000,
    border: `2px solid ${mixins.lightTextColor2}`,
    color: mixins.lightTextColor2,
    padding: '16px 24px',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: '16px',
    textTransform: 'none',
    backgroundColor: 'transparent',
    maxHeight: 48,

    '& span': {
      width: '100%',
      display: 'inherit',
      minWidth: 104,
      alignItems: 'inherit',
      justifyContent: 'inherit'
    },

    '&.MuiButton-text, &.MuiButton-textPrimary': {
      color: mixins.lightTextColor2
    },

    '&:hover, &.MuiButton-text:hover': {
      backgroundColor: mixins.lightThemeBgColor2,
      color: mixins.lightTextColor2
    }
  },
  saveButton: {
    borderRadius: 1000,
    backgroundColor: mixins.amethyst,
    color: mixins.chalkWhiteFont,
    padding: '16px 24px',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: '16px',
    textTransform: 'none',
    maxHeight: 48,
    minWidth: 'unset',
    border: 'none',

    '&.MuiButton-text, &.MuiButton-textPrimary': {
      backgroundColor: mixins.amethyst,
      background: mixins.amethyst,
      color: mixins.chalkWhiteFont,
      fontFamily: mixins.fontFamilyPlusSans
    },

    '& span': {
      width: '100%',
      display: 'inherit',
      minWidth: 104,
      alignItems: 'inherit',
      justifyContent: 'inherit'
    },

    '&:hover, &.MuiButton-text:hover': {
      backgroundColor: mixins.amethystColorV2,
      background: mixins.amethystColorV2,
      color: mixins.chalkWhiteFont
    },
    '&:disabled': {
      backgroundColor: mixins.bulkEditCtaButtonDisabledBGColor,
      color: mixins.disabledTextColor
    }
  },
  editPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0
  },
  editSectionsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    alignItems: 'center',
    marginBottom: 16
  },
  editSectionsPrefix: {
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 12,
    fontWeight: mixins.fontWeight500,
    lineHeight: '20px',
    color: mixins.lightTextColor2,
    flexShrink: 0
  },
  editSectionChip: {
    backgroundColor: mixins.feedBackColor,
    borderRadius: 4,
    padding: '2px 4px',
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: 12,
    fontWeight: mixins.fontWeight500,
    lineHeight: '20px',
    color: mixins.timeIconColor,
    flexShrink: 0
  }
};

export const classes = createScopedClasses('aet-assignment-form', assignmentFormStyles);

export const scopedStyles = createScopedStyles(assignmentFormStyles, classes);

export const AssignmentFormStylesRoot = styled('div')(scopedStyles);

export default assignmentFormStyles;
